<template>
  <v-card class="mx-auto" elevation="16" max-width="100vw">
    <v-card-item>
      <v-card-title> Noticias</v-card-title>
      <v-card-subtitle>
        <v-row>
          <v-col cols="12"> Administración de Noticias </v-col>
        </v-row>

        <v-row style="margin-left: 16px; margin-right: 16px">
          <v-col cols="6">
            <v-text-field v-model="search" append-icon="fa-solid fa-search" label="Buscar" single-line hide-details
              density="comfortable" />
          </v-col>

          <v-col cols="6" class="text-right">
            <v-btn class="btn-add-user" @click="newNews">
              <v-icon class="fa-solid fa-plus"></v-icon>
              Nueva Noticia
            </v-btn>
          </v-col>
        </v-row>
      </v-card-subtitle>
    </v-card-item>
    <v-card-text>
      <v-data-table v-model:items-per-page="itemsPerPage" :headers="headers" :items="newsContent" :loading="loading"
        item-value="name" @update:options="loadItems">
        <template #item="{ item }">
          <tr>
            <td>{{ item.id }}</td>
            <td>
              <span :class="getStatusBadge(item.status).badgeClass">
                {{ getStatusBadge(item.status).text }}
              </span>
            </td>
            <td>{{ item.title }}</td>
            <td v-html="maxLengthDescription(item.description)"></td>
            <td>{{ item.author }}</td>
            <td>{{ item.year }}</td>
            <td>{{ dataFormat(item.created_at) }}</td>
            <td>{{ dataFormat(item.updated_at) }}</td>
            <td>
              <template v-if="item.status === 'ACTIVE'">
                <v-tooltip text="Eliminar" location="top">
                  <template v-slot:activator="{ props }">
                    <v-btn v-bind="props" @click="deleteNew(item)" class="btn-accion" icon>
                      <v-icon class="fa-solid fa-trash"></v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
              </template>
              <template v-else>
                <v-tooltip text="Activar" location="top">
                  <template v-slot:activator="{ props }">
                    <v-btn v-bind="props" @click="deleteNew(item)" class="btn-accion" icon>
                      <v-icon class="fa-solid fa-circle-check"></v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
              </template>
              <v-tooltip text="Editar" location="top">
                <template v-slot:activator="{ props }">
                  <v-btn v-bind="props" class="btn-accion" icon @click="editNews(item)">
                    <v-icon class="fa-solid fa-pencil"></v-icon>
                  </v-btn>
                </template>
              </v-tooltip>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
  <ModalAddNew :is-visible-news="showModalAddNews" @update:visible="showModalAddNews = $event"
    @update:news="loadItems()" />
  <ModalUpdateNew :is-visible-update-news="showModalEditNews" :news="selectedNews"
    @update:visible="showModalEditNews = $event" @update:news="loadItems()" />
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { getNewsForAdmin, deleteNews } from "../services/news-service";
import ModalAddNew from "../components/ModalAddNew.vue";
import ModalUpdateNew from "../components/ModalUpdateNew.vue";
import { showSuccessToast } from "@/kernel/alerts";
import { formatDate } from "@/kernel/utils";

const news = ref({
  content: [],
  totalElements: 0,
  page: 0,
  size: 25,
});

const itemsPerPage = ref(25);
const loading = ref(false);
const search = ref("");
const showModalAddNews = ref(false);
const showModalEditNews = ref(false);
const selectedNews = ref(null);

const dataFormat = (date) => {
  return formatDate(date);
};

const headers = ref([
  { title: "#", key: "id" },
  { title: "Status", key: "status" },
  { title: "Titulo", key: "title" },
  { title: "Descripción", key: "description" },
  { title: "Autor", key: "author" },
  { title: "Año", key: "year" },
  { title: "Subido el", key: "created_at" },
  { title: "Última actualización", key: "updated_at" },
  { title: "Acciones", key: "actions", sortable: false },
]);

const newsContent = computed(() => {
  if (!search.value) return news.value.content;

  const lowerSearch = search.value.toLowerCase();

  return news.value.content.filter((item) => {
    return (
      item.title?.toLowerCase().includes(lowerSearch) ||
      item.description?.toLowerCase().includes(lowerSearch) ||
      item.author?.toLowerCase().includes(lowerSearch) ||
      item.year?.toString().includes(lowerSearch) ||
      item.status?.toLowerCase().includes(lowerSearch)
    );
  });
});


onMounted(() => {
  loadItems();
});

const loadItems = async () => {
  loading.value = true;
  try {
    const params = {
      page: news.value.page,
      size: itemsPerPage.value,
      search: search.value,
    };
    const result = await getNewsForAdmin(params);
    news.value.content = result.data;
  } catch (error) {
    console.log(error);
  } finally {
    loading.value = false;
  }
};

const deleteNew = async (params) => {
  const user_status = params.status === "ACTIVE" ? "se desactivo" : "se activo";
  const data = {
    id: params.id,
  }
  const result = await deleteNews(data);
  if (result.status === 200) {
    loadItems();
    showSuccessToast(`La noticia ${user_status} correctamente`);
  }
};

const editNews = (news) => {
  selectedNews.value = news;
  showModalEditNews.value = true;
};

const newNews = async () => {
  showModalAddNews.value = true;
};

const maxLengthDescription = (description) => {
  return description.length > 50 ? description.substring(0, 50) + '...' : description;
};

const getStatusBadge = (status) => {
  if (!status) {
    return {
      text: "Desconocido",
      badgeClass: "badge-unknown",
    };
  }

  let text = "";
  let badgeClass = "";

  switch (status.toUpperCase()) {
    case "ACTIVE":
      text = "Activo";
      badgeClass = "badge-active";
      break;
    case "INACTIVE":
      text = "Inactivo";
      badgeClass = "badge-inactive";
      break;
    default:
      text = status;
      badgeClass = "badge-default";
      break;
  }

  return {
    text,
    badgeClass,
    showModalAddNews,
  };
};
</script>

<style scoped>
.badge-active {
  background-color: yellowgreen;
  color: white;
  padding: 5px 10px;
  border-radius: 12px;
}

.badge-inactive {
  background-color: red;
  color: white;
  padding: 5px 10px;
  border-radius: 12px;
}

.badge-unknown {
  background-color: orange;
  color: white;
  padding: 5px 10px;
  border-radius: 12px;
}

.badge-default {
  background-color: blue;
  color: white;
  padding: 5px 10px;
  border-radius: 12px;
}



.fa-trash:hover {
  color: red;
}

.fa-pencil:hover {
  color: rgb(34, 99, 127);
}

.fa-eye:hover {
  color: rgb(79, 127, 34);
}

.fa-circle-check:hover {
  color: green;
}

.btn-add-user {
  background-color: black;
  color: white;
}

.btn-add-user:hover {
  background-color: orange;
  color: #000;
}
</style>